import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, isDevMode, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';
import { GoogleMapsModule } from '@angular/google-maps';
import { FormsModule } from '@angular/forms';
import en from '@angular/common/locales/en';

import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';

import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './shared/shared.module';
import { NgApexchartsModule } from 'ng-apexcharts';
import { AppComponent } from './app.component';

import { tokenInterceptor } from './core/interceptors/token.interceptor';

import { authFeature } from './modules/auth/state/auth.state';
import { createLocalStorageSyncReducer } from './state/meta-reducer';
import { locationFeature } from './modules/location-tree/state/location-tree.state';
import { assetsFeature } from './modules/assets/state/assets.state';
import { reportFeature } from './modules/reports/state/report.state';
import { appFeature } from './state/app.state';

import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { FullScreenLayoutComponent } from './layouts/full-screen-layout/full-screen-layout.component';
import { GetSupportComponent } from './modules/profile/get-support/get-support.component';
import { UserProfileComponent } from './modules/profile/user-profile/user-profile.component';
import { TrainingVideosComponent } from './shared/components/training-videos/training-videos.component';

import { provideNzWave } from 'ng-zorro-antd/core/wave';
import { NzConfig, provideNzConfig } from 'ng-zorro-antd/core/config';
import { NZ_I18N } from 'ng-zorro-antd/i18n';
import { en_US } from 'ng-zorro-antd/i18n';
import { NZ_ICONS } from 'ng-zorro-antd/icon';
import { IconDefinition } from '@ant-design/icons-angular';
import {
  AppstoreOutline, ArrowLeftOutline, ArrowRightOutline, BarChartOutline,
  BarcodeOutline, BellOutline, BulbOutline, CalendarOutline, CarOutline,
  CaretDownOutline, CheckOutline, CheckCircleOutline, ClockCircleOutline,
  CloseOutline, CloseCircleOutline, CloudUploadOutline, ClusterOutline,
  CommentOutline, CompassOutline, CustomerServiceOutline, DeleteOutline,
  DownOutline, DownCircleOutline, DownloadOutline, EditOutline,
  EnvironmentOutline, ExportOutline, EyeOutline, FileDoneOutline,
  FileExcelOutline, FilePdfOutline, FileProtectOutline, FileSearchOutline,
  FileTextOutline, FilterOutline, FireOutline, FlagOutline, FolderOpenOutline,
  FormOutline, HeartOutline, HeatMapOutline, HomeOutline, InboxOutline,
  InfoCircleOutline, LineChartOutline, LoadingOutline, LockOutline,
  MailOutline, MenuOutline, MessageOutline, MinusOutline, PauseCircleOutline,
  PhoneOutline, PlayCircleOutline, PlusOutline, ProductOutline, PushpinOutline,
  RedoOutline, ReloadOutline, RightOutline, RocketOutline, SearchOutline,
  SendOutline, SettingOutline, SignatureOutline, SoundOutline, StockOutline,
  StopOutline, SwapOutline, SyncOutline, TableOutline, UnorderedListOutline,
  UpCircleOutline, UploadOutline, UserOutline, UserAddOutline, WarningOutline,
} from '@ant-design/icons-angular/icons';

const NZ_ICONS_LIST: IconDefinition[] = [
  AppstoreOutline, ArrowLeftOutline, ArrowRightOutline, BarChartOutline,
  BarcodeOutline, BellOutline, BulbOutline, CalendarOutline, CarOutline,
  CaretDownOutline, CheckOutline, CheckCircleOutline, ClockCircleOutline,
  CloseOutline, CloseCircleOutline, CloudUploadOutline, ClusterOutline,
  CommentOutline, CompassOutline, CustomerServiceOutline, DeleteOutline,
  DownOutline, DownCircleOutline, DownloadOutline, EditOutline,
  EnvironmentOutline, ExportOutline, EyeOutline, FileDoneOutline,
  FileExcelOutline, FilePdfOutline, FileProtectOutline, FileSearchOutline,
  FileTextOutline, FilterOutline, FireOutline, FlagOutline, FolderOpenOutline,
  FormOutline, HeartOutline, HeatMapOutline, HomeOutline, InboxOutline,
  InfoCircleOutline, LineChartOutline, LoadingOutline, LockOutline,
  MailOutline, MenuOutline, MessageOutline, MinusOutline, PauseCircleOutline,
  PhoneOutline, PlayCircleOutline, PlusOutline, ProductOutline, PushpinOutline,
  RedoOutline, ReloadOutline, RightOutline, RocketOutline, SearchOutline,
  SendOutline, SettingOutline, SignatureOutline, SoundOutline, StockOutline,
  StopOutline, SwapOutline, SyncOutline, TableOutline, UnorderedListOutline,
  UpCircleOutline, UploadOutline, UserOutline, UserAddOutline, WarningOutline,
];

registerLocaleData(en);
const ngZorroConfig: NzConfig = {
  message: {
    nzTop: 50,
    nzMaxStack: 1,
  },
  pagination: {
    nzPageSizeOptions: [10, 20, 30, 40, 50],
    nzShowSizeChanger: true,
    nzSize: "small",
  },
  table: {
    nzSize: "small",
    nzBordered: false,
  },
  modal: {
    nzMaskClosable: false,
    nzCloseOnNavigation: true,
    nzMask: true,
  },
  drawer: {
    nzCloseOnNavigation: true,
    nzMaskClosable: false,
    nzMask: true,
  }
};

interface RootState {
  [appFeature.name]: ReturnType<typeof appFeature.reducer>;
  [authFeature.name]: ReturnType<typeof authFeature.reducer>;
  [locationFeature.name]: ReturnType<typeof locationFeature.reducer>;
  [assetsFeature.name]: ReturnType<typeof assetsFeature.reducer>;
  [reportFeature.name]: ReturnType<typeof reportFeature.reducer>;
}

@NgModule({
  declarations: [
    AppComponent,
    MainLayoutComponent,
    FullScreenLayoutComponent,
    GetSupportComponent,
    UserProfileComponent,
    TrainingVideosComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    FormsModule,
    NgApexchartsModule,
    GoogleMapsModule,
    StoreModule.forRoot(
      {
        [appFeature.name]: appFeature.reducer,
        [authFeature.name]: authFeature.reducer,
        [locationFeature.name]: locationFeature.reducer,
        [assetsFeature.name]: assetsFeature.reducer,
        [reportFeature.name]: reportFeature.reducer,
      },
      {
        metaReducers: [
          createLocalStorageSyncReducer<RootState>('app', appFeature.name),
          createLocalStorageSyncReducer<RootState>('auth', authFeature.name),
          createLocalStorageSyncReducer<RootState>('location', locationFeature.name),
          createLocalStorageSyncReducer<RootState>('assets', assetsFeature.name),
          createLocalStorageSyncReducer<RootState>('report', reportFeature.name),
        ],
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
        },
      }
    ),
    EffectsModule.forRoot(),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true, // If set to true, the connection is established within the Angular zone
    }),
  ],
  providers: [
    { provide: NZ_I18N, useValue: en_US },
    { provide: NZ_ICONS, useValue: NZ_ICONS_LIST },
    provideNzWave({ disabled: true }),
    provideNzConfig(ngZorroConfig),
    provideAnimationsAsync('animations'),
    provideHttpClient(withInterceptors([tokenInterceptor])),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule { }
